import React, { useState } from "react";
import {
  AppBar,
  Backdrop,
  Box,
  Dialog,
  Fab,
  IconButton,
  InputAdornment,
  Slide,
  TextField,
  Toolbar,
  Typography,
  debounce,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import { Add, Close, Search } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";

import { LoadingBox } from "../../../../../components/utils";

import FunctionsTable from "./components/FunctionsTable";
import { FunctionsDialog } from "./components/FunctionsDialog";

import { getFunctions, getFunction } from "../../../../services/pwsApi";

const useStyles = makeStyles(theme => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "primary",
    backgroundColor: "rgba(0, 0, 0, 0.25) !important",
  },
  fab: {
    position: "fixed",
    bottom: theme.spacing(10),
    right: theme.spacing(2),
  },
}));

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function Functions() {
  const classes = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const companyCode = searchParams.get("company_code");
  const functionId = searchParams.get("function_id");

  const tablePageIndex = Number(searchParams.get("page") ?? 1);
  const tablePageSize = Number(searchParams.get("page_size") ?? 50);

  const functionsQuery = useQuery(
    ["functions", companyCode, tablePageIndex, tablePageSize, searchQuery],
    () =>
      getFunctions(
        companyCode.slice(0, 3),
        companyCode.slice(3, 7),
        searchQuery,
        {
          page: tablePageIndex,
          perPage: tablePageSize,
        },
      ),
    {
      enabled: !!companyCode,
    },
  );

  const functionQuery = useQuery(
    ["function", functionId],
    () =>
      getFunction(companyCode.slice(0, 3), companyCode.slice(3, 7), functionId),
    {
      enabled: !!functionId,
      onSuccess: () => setDialogOpen(true),
    },
  );

  let singleFunction = functionQuery.data?.data?.data;
  singleFunction = singleFunction
    ? {
        functionId: singleFunction.R_E_C_N_O_,
        code: singleFunction.RJ_FUNCAO,
        description: singleFunction.RJ_DESC,
        cbo: singleFunction.RJ_CODCBO,
      }
    : null;

  const handleCloseDialog = () => {
    searchParams.delete("screen");
    setSearchParams(searchParams);
  };

  const handleCloseFunctionsDialog = () => {
    searchParams.delete("function_id");
    setSearchParams(searchParams);
    setDialogOpen(false);
  };

  const handleCreateFunction = () => {
    setDialogOpen(true);
  };

  const searchDelayed = debounce(query => {
    setSearchQuery(query);
  }, 1000);

  const functions = functionsQuery?.data?.data?.data ?? [];
  const rows = functionsQuery?.data?.data?.meta?.total ?? 0;

  return (
    <Dialog
      fullScreen
      open
      onClose={handleCloseDialog}
      TransitionComponent={Transition}
    >
      {dialogOpen && (
        <FunctionsDialog
          onClose={handleCloseFunctionsDialog}
          data={singleFunction}
        />
      )}

      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleCloseDialog}
            aria-label="close"
          >
            <Close />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Funções
          </Typography>
        </Toolbar>
      </AppBar>

      <Backdrop
        className={classes.backdrop}
        open={functionsQuery.isLoading || false}
      >
        <LoadingBox />
      </Backdrop>

      <Box sx={{ height: "calc(100% - 94px)", padding: "15px" }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <TextField
            style={{ width: "400px" }}
            label="Pesquisar"
            variant="outlined"
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            onChange={event => {
              searchDelayed(event.target.value);
            }}
          />
        </Box>

        <FunctionsTable data={functions} rows={rows} />
      </Box>

      <Fab
        color="primary"
        aria-label="add"
        className={classes.fab}
        onClick={handleCreateFunction}
      >
        <Add />
      </Fab>
    </Dialog>
  );
}
